/**
 * FikoFeatureTani Dialog — Otomatik İşleme Özelliği Tanıma UI
 * Modeli tara → delik/cep/düzlem/pah listele → tıkla vurgula → tek tıkla CAM operasyonu.
 */
import { Component, Input, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { ozellikleriTani, ozellikOzet, ITanananOzellik, OzellikTip } from './FikoFeatureTani';
import { baglamYaz } from '../fiko-uretim-baglam/UretimBaglam';

const TIP_BILGI: Record<OzellikTip, { ad: string; simge: string; renk: string }> = {
  delik:       { ad: 'Delik', simge: '⊙', renk: '#33c2ff' },
  cep:         { ad: 'Cep', simge: '▭', renk: '#ffb347' },
  duzlemUst:   { ad: 'Üst Düzlem', simge: '▱', renk: '#7dffc0' },
  pah:         { ad: 'Pah/Konik', simge: '◣', renk: '#d59bff' },
  silindirDis: { ad: 'Silindir', simge: '◯', renk: '#9aa7b4' },
};

@Component({
  selector: 'fikoyt-feature-tani',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
  <div class="ft">
    <button class="ft-btn primary" (click)="tara()">🔍 Modeli Tara</button>
    @if (tarandi) {
      <div class="ft-ozet">
        @for (k of tipler; track k) {
          @if (ozet[k]) { <span class="ft-chip" [style.borderColor]="bilgi[k].renk"><b [style.color]="bilgi[k].renk">{{ bilgi[k].simge }}</b> {{ bilgi[k].ad }}: {{ ozet[k] }}</span> }
        }
      </div>
      @if (!liste.length) { <div class="ft-bos">Tanınan özellik yok (delik/cep/düzlem bulunamadı).</div> }
      <div class="ft-liste">
        @for (o of liste; track $index) {
          <div class="ft-sat" [class.aktif]="seciliIdx===$index" (click)="sec($index)">
            <span class="ft-s" [style.color]="bilgi[o.tip].renk">{{ bilgi[o.tip].simge }}</span>
            <span class="ft-et">{{ o.etiket }}</span>
          </div>
        }
      </div>
      <div class="ft-cam">
        @if (ozet.delik) { <button class="ft-btn" (click)="camGonder('delik')">🛠️ Delikleri CAM'e Gönder ({{ ozet.delik }})</button> }
        @if (ozet.cep || ozet.duzlemUst) { <button class="ft-btn" (click)="camGonder('yuzey')">🛠️ Cep/Yüzey CAM Aç</button> }
        @if (ana && ozet.delik) { <button class="ft-btn" (click)="ilgiliAc('acMuayeneDialog')">✓ Muayene Formu Oluştur →</button> }
      </div>
    }
    @if (hata) { <div class="ft-hata">{{ hata }}</div> }
  </div>
  `,
  styles: [`
    .ft { padding:10px; min-width:240px; max-width:280px; font-size:11px; color:#e6f6ff;
      background:linear-gradient(180deg,rgba(0,40,60,.96),rgba(0,18,36,.98)); border-radius:6px; }
    .ft-btn { width:100%; padding:8px; margin-top:4px; border:1px solid rgba(0,200,220,.5); background:rgba(0,70,90,.7); color:#fff; border-radius:5px; font-weight:600; cursor:pointer; transition:.15s; }
    .ft-btn:hover { background:rgba(0,110,135,.85); }
    .ft-btn.primary { background:linear-gradient(90deg,#0a84c0,#0bb39e); border:none; }
    .ft-ozet { display:flex; flex-wrap:wrap; gap:4px; margin:10px 0 6px; }
    .ft-chip { font-size:10px; padding:3px 6px; background:rgba(0,0,0,.3); border:1px solid; border-radius:10px; }
    .ft-liste { max-height:220px; overflow:auto; margin:4px 0; border:1px solid rgba(0,200,220,.2); border-radius:5px; }
    .ft-sat { display:flex; align-items:center; gap:7px; padding:5px 7px; cursor:pointer; border-bottom:1px solid rgba(0,200,220,.1); }
    .ft-sat:hover { background:rgba(0,120,150,.25); }
    .ft-sat.aktif { background:rgba(0,200,120,.2); }
    .ft-s { font-size:14px; width:16px; text-align:center; }
    .ft-et { flex:1; }
    .ft-bos { padding:8px; text-align:center; color:#9fe8f7; opacity:.7; }
    .ft-cam { margin-top:6px; display:flex; flex-direction:column; gap:4px; }
    .ft-hata { margin-top:8px; padding:6px; background:rgba(255,50,50,.2); border:1px solid rgba(255,100,100,.4); border-radius:4px; color:#ff9a9a; text-align:center; }
  `]
})
export class FikoFeatureTaniComponent implements OnDestroy {
  @Input() anaGosterim: any = null;
  @Input() pencere: any;
  @Input() ayarlar: any = {};
  @Input() modelleme: any = null;                    // 3D modelleme yöneticisi (govdeMesh kaynağı)
  @Input() camAc: ((tip: 'delik' | 'yuzey', liste: ITanananOzellik[]) => void) | null = null;  // CAM köprüsü
  @Input() ana: any = null;                          // parent 3D component (çapraz-link)

  tipler: OzellikTip[] = ['delik', 'cep', 'duzlemUst', 'pah', 'silindirDis'];
  bilgi = TIP_BILGI;
  liste: ITanananOzellik[] = [];
  ozet: Record<OzellikTip, number> = { delik: 0, cep: 0, duzlemUst: 0, pah: 0, silindirDis: 0 };
  tarandi = false; seciliIdx = -1; hata: string | null = null;
  private marker: THREE.Group | null = null;

  constructor(private cd: ChangeDetectorRef) {}
  ngOnDestroy(): void { this.markerTemizle(); }

  private govdeMeshBul(): any {
    const md = this.modelleme || this.anaGosterim?.modelleme;
    if (md?.govdeMesh) return md.govdeMesh;
    let best: any = null, bestTri = 0;
    this.anaGosterim?.sahneler?.[0]?.traverse?.((o: any) => {
      if (o.isMesh && o.visible && o.geometry?.attributes?.['grup']) { const t = o.geometry.attributes.position?.count || 0; if (t > bestTri) { bestTri = t; best = o; } }
    });
    return best;
  }

  tara(): void {
    this.hata = null; this.markerTemizle();
    const mesh = this.govdeMeshBul();
    if (!mesh) { this.hata = 'Önce bir model yükleyin/oluşturun'; this.tarandi = true; this.liste = []; this.cd.detectChanges(); return; }
    try {
      this.liste = ozellikleriTani(mesh);
      this.ozet = ozellikOzet(this.liste) as any;
      this.tarandi = true; this.seciliIdx = -1;
      const delikler = this.liste.filter(o => o.tip === 'delik' && o.cap).map(o => +o.cap!.toFixed(1));
      baglamYaz({ delikler });   // tanınan delikler → muayene/iş emri okur
    } catch (e: any) { this.hata = e.message || 'Tarama hatası'; }
    this.cd.detectChanges();
  }

  sec(i: number): void {
    this.seciliIdx = i; const o = this.liste[i]; if (!o) return;
    this.markerGoster(o);
    this.cd.detectChanges();
  }

  private markerGoster(o: ITanananOzellik): void {
    this.markerTemizle();
    const sahne = this.anaGosterim?.sahneler?.[0]; if (!sahne) return;
    const renk = parseInt((TIP_BILGI[o.tip].renk).slice(1), 16);
    const g = new THREE.Group(); g.name = 'FikoFeatMarker';
    const boyut = o.cap ? Math.max(o.cap * 0.6, 4) : Math.max((o.alan ? Math.sqrt(o.alan * 100) : 8), 6);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(boyut, boyut * 0.12, 8, 32), new THREE.MeshBasicMaterial({ color: renk, transparent: true, opacity: 0.9, depthTest: false }));
    const kure = new THREE.Mesh(new THREE.SphereGeometry(boyut * 0.25, 12, 12), new THREE.MeshBasicMaterial({ color: renk, depthTest: false }));
    g.add(ring); g.add(kure);
    g.position.set(o.konum.x, o.konum.y, o.konum.z);
    g.renderOrder = 999;
    sahne.add(g); this.marker = g;
    this.anaGosterim?.ciz?.();
  }

  private markerTemizle(): void {
    if (this.marker) { (this.marker.parent || this.anaGosterim?.sahneler?.[0])?.remove(this.marker); this.marker.traverse((o: any) => { o.geometry?.dispose?.(); o.material?.dispose?.(); }); this.marker = null; this.anaGosterim?.ciz?.(); }
  }

  camGonder(tip: 'delik' | 'yuzey'): void {
    if (typeof this.camAc === 'function') { this.camAc(tip, this.liste); }
    else this.hata = 'CAM köprüsü bulunamadı';
    this.cd.detectChanges();
  }

  /** Çapraz-link: ilgili modülü aç (tanınan delikler bağlamda → muayene ön-dolu). */
  ilgiliAc(metod: string): void { try { this.ana?.[metod]?.(); } catch { } }
}
